/**
 * MAE / Maybank2u receipt-text parser.
 *
 * Input is text the user pasted, or text pulled out of a screenshot or PDF on
 * their own device. This module never touches the network and never sees
 * credentials -- a receipt is just text.
 *
 * The parser is intentionally shy: if it cannot find an amount it fails rather
 * than inventing one, and anything it is unsure about lowers the confidence
 * score so the record lands in the review inbox.
 */
import { DEFAULT_TIME_ZONE } from '../time.js';
import type { TransactionType } from '../schemas.js';
import {
  combineConfidence,
  datePartsToIso,
  findAmounts,
  normalizeReceiptText,
  parseReceiptDate,
  valueAfterLabel,
  valueBelowLabel,
  type Extracted,
  type ParseOptions,
  type ParseResult,
} from './common.js';

const SIGNATURES = [
  'maybank',
  'maybank2u',
  'mae by maybank',
  'mae wallet',
  'm2u',
  'mb2u',
  'duitnow',
  'maybank islamic',
];

const AMOUNT_LABELS = ['amount', 'total amount', 'amount paid', 'total', 'jumlah'];
const MERCHANT_LABELS = [
  'merchant',
  'merchant name',
  'to',
  'to account',
  'recipient',
  'recipient name',
  'beneficiary',
  'beneficiary name',
  'payee',
  'pay to',
  'paid to',
  'transfer to',
  'penerima',
  // A refund receipt names the payer, not a payee.
  'from',
];
const DATE_LABELS = [
  'transaction date',
  'date & time',
  'date and time',
  'date/time',
  'date',
  'tarikh',
  'transaction date & time',
];
const REFERENCE_LABELS = [
  'reference id',
  'reference no',
  'reference number',
  'ref no',
  'ref id',
  'ref',
  'transaction id',
  'transaction no',
  'receipt no',
  'rujukan',
];

/** Lines that are never a merchant, however they are labelled. */
const MERCHANT_BLOCKLIST = [
  'maybank',
  'maybank2u',
  'mae',
  'mae wallet',
  'duitnow',
  'successful',
  'transaction successful',
  'transfer successful',
  'payment successful',
  'savings account',
  'current account',
];

export function looksLikeMaybankReceipt(raw: string): boolean {
  const text = raw.toLowerCase();
  return SIGNATURES.some((signature) => text.includes(signature));
}

function detectType(lines: readonly string[]): Extracted<TransactionType> | undefined {
  const haystack = lines.join(' \n ').toLowerCase();

  const checks: { pattern: RegExp; type: TransactionType; confidence: number }[] = [
    { pattern: /\b(refund|reversal|reversed|chargeback)\b/, type: 'refund', confidence: 0.9 },
    { pattern: /\b(duitnow transfer|transfer to|fund transfer|instant transfer|ibg)\b/, type: 'transfer', confidence: 0.9 },
    { pattern: /\b(reload|top[- ]?up|tambah nilai)\b/, type: 'transfer', confidence: 0.85 },
    { pattern: /\b(money received|received from|credited|incoming)\b/, type: 'income', confidence: 0.85 },
    { pattern: /\b(payment|paid|purchase|qrpay|qr pay|bill payment|pembayaran)\b/, type: 'expense', confidence: 0.85 },
    { pattern: /\btransfer\b/, type: 'transfer', confidence: 0.7 },
  ];

  for (const check of checks) {
    const match = check.pattern.exec(haystack);
    if (match) {
      const evidence = lines.find((line) => check.pattern.test(line.toLowerCase())) ?? match[0];
      return { value: check.type, confidence: check.confidence, evidence };
    }
  }
  return undefined;
}

function extractAmount(lines: readonly string[]): Extracted<number> | undefined {
  // A labelled amount is the most trustworthy.
  for (const [index, line] of lines.entries()) {
    const labelled = valueAfterLabel(line, AMOUNT_LABELS);
    if (labelled) {
      const [amount] = findAmounts(labelled);
      if (amount !== undefined && amount > 0) {
        return { value: amount, confidence: 0.97, evidence: line };
      }
    }
    // "Amount" on its own line, value underneath.
    if (AMOUNT_LABELS.includes(line.toLowerCase().replace(/[:\s]+$/, ''))) {
      const below = valueBelowLabel(lines, index);
      const [amount] = below ? findAmounts(below) : [];
      if (amount !== undefined && amount > 0) {
        return { value: amount, confidence: 0.95, evidence: `${line} / ${below ?? ''}`.trim() };
      }
    }
  }

  // Otherwise the first explicit "RM ..." line wins. Skip balance lines, which
  // are a different number entirely.
  const candidates = lines
    .filter((line) => !/\b(balance|baki|available|limit|fee|charge)\b/i.test(line))
    .flatMap((line) =>
      findAmounts(line)
        .filter((amount) => amount > 0)
        .map((amount) => ({ amount, line })),
    )
    .filter((entry) => /RM|MYR/i.test(entry.line));

  const first = candidates[0];
  if (!first) return undefined;
  // Several unlabelled amounts means we may have picked the wrong one.
  return {
    value: first.amount,
    confidence: candidates.length === 1 ? 0.88 : 0.7,
    evidence: first.line,
  };
}

function isBlockedMerchant(value: string): boolean {
  const lower = value.toLowerCase().trim();
  return MERCHANT_BLOCKLIST.some((blocked) => lower === blocked || lower.startsWith(`${blocked} `));
}

function extractMerchant(lines: readonly string[]): Extracted<string> | undefined {
  for (const [index, line] of lines.entries()) {
    const labelled = valueAfterLabel(line, MERCHANT_LABELS);
    if (labelled && !isBlockedMerchant(labelled) && !/^RM\b/i.test(labelled)) {
      // "To: 1234567890 (Example Bank)" -- keep the name, drop the number.
      const cleaned = labelled.replace(/\b\d{8,}\b/g, '').replace(/\s{2,}/g, ' ').trim();
      if (cleaned.length >= 2) {
        return { value: cleaned, confidence: 0.92, evidence: line };
      }
    }
    const bare = line.toLowerCase().replace(/[:\s]+$/, '');
    if (MERCHANT_LABELS.includes(bare)) {
      const below = valueBelowLabel(lines, index);
      if (below && !isBlockedMerchant(below) && !/^RM\b/i.test(below)) {
        return { value: below, confidence: 0.88, evidence: `${line} / ${below}` };
      }
    }
  }
  return undefined;
}

function extractDate(
  lines: readonly string[],
  options: ParseOptions,
): Extracted<string> | undefined {
  const timeZone = options.timeZone ?? DEFAULT_TIME_ZONE;

  for (const line of lines) {
    const labelled = valueAfterLabel(line, DATE_LABELS);
    if (!labelled) continue;
    const parsed = parseReceiptDate(labelled, options);
    if (parsed) {
      return {
        value: datePartsToIso(parsed.parts, timeZone),
        confidence: Math.min(0.97, parsed.confidence + 0.02),
        evidence: line,
      };
    }
  }

  for (const line of lines) {
    const parsed = parseReceiptDate(line, options);
    if (parsed) {
      return {
        value: datePartsToIso(parsed.parts, timeZone),
        confidence: parsed.confidence * 0.95,
        evidence: line,
      };
    }
  }
  return undefined;
}

function extractReference(lines: readonly string[]): Extracted<string> | undefined {
  for (const line of lines) {
    const labelled = valueAfterLabel(line, REFERENCE_LABELS);
    if (labelled && /[A-Za-z0-9]{6,}/.test(labelled)) {
      return { value: labelled.slice(0, 64), confidence: 0.95, evidence: line };
    }
  }
  return undefined;
}

/**
 * Parse MAE / Maybank2u receipt text.
 * Returns `{ ok: false }` when there is no amount -- no amount, no transaction.
 */
export function parseMaybankReceipt(raw: string, options: ParseOptions = {}): ParseResult {
  const lines = normalizeReceiptText(raw);
  if (lines.length === 0) {
    return { ok: false, parser: 'maybank', reason: 'The receipt text is empty.' };
  }

  const amount = extractAmount(lines);
  if (!amount) {
    return {
      ok: false,
      parser: 'maybank',
      reason: 'No Ringgit amount found in this receipt. Nothing was imported.',
    };
  }

  const merchant = extractMerchant(lines);
  const occurredAt = extractDate(lines, options);
  const reference = extractReference(lines);
  const type = detectType(lines);

  const warnings: string[] = [];
  if (!merchant) warnings.push('No merchant or recipient found.');
  if (!occurredAt) warnings.push('No transaction date found; the import time will be used.');
  if (!type) warnings.push('Could not tell whether this is a payment or a transfer.');
  if (!looksLikeMaybankReceipt(raw)) {
    warnings.push('This does not clearly look like a Maybank or MAE receipt.');
  }

  const scores = [
    amount.confidence,
    merchant?.confidence ?? 0.35,
    occurredAt?.confidence ?? 0.3,
    type?.confidence ?? 0.5,
  ];

  return {
    ok: true,
    parser: 'maybank',
    amountSen: amount,
    ...(merchant ? { merchant } : {}),
    ...(occurredAt ? { occurredAt } : {}),
    ...(reference ? { reference } : {}),
    ...(type ? { type } : {}),
    currency: 'MYR',
    confidence: combineConfidence(scores),
    warnings,
  };
}
